import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Avatar } from '../../../components/Avatar';
import { Badge } from '../../../components/Badge';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useAiReportDatasets } from '../../../hooks/useAiReportDataset';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const DatasetsPage: React.FC = () => {
    const { datasets, loading, error, fetchDatasets, deleteDataset } = useAiReportDatasets();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage] = useState<number>(10);
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('az');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');

    useEffect(() => {
        fetchDatasets();
    }, [fetchDatasets]);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
    };

    const availableYears = Array.from(
        new Set(
            datasets
                .map((d) => new Date(d.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
    ];

    const filtered = datasets
        .filter((d) => {
            const matchesSearch = d.name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStartDate = !filterStartDate || new Date(d.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(d.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(d.created_time).getFullYear().toString() === filterYear;
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear;
        })
        .sort((a, b) => sortOrder === 'az' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));

    const indexOfLast = currentPage * perPage;
    const indexOfFirst = indexOfLast - perPage;
    const currentDatasets = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / perPage);

    const handleOpenDeleteDialog = (id: number) => {
        setSelectedId(id);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (selectedId === null) return;
        setIsDeleting(true);
        try {
            await deleteDataset(selectedId);
            showSuccessToast('Data set deleted');
            setIsOpen(false);
        } catch (err) {
            showErrorToast('Failed to delete data set');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Link
                to="/gp/ai-report-helper"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to AI Report Helper
            </Link>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Data Sets</Heading>
                    <p className="mt-2 text-xs text-zinc-500">
                        Named lookup tables (e.g. customer address/area) with one referred column, used to bulk-fill
                        report columns when converting a report.
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button onClick={() => navigate('/gp/ai-report-helper/datasets/add')}>
                        Create new data set
                    </Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search by name…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {!loading && !error && currentDatasets.length === 0 && <p className='py-16 text-center'>No data sets found.</p>}
                {!loading && !error && currentDatasets.map((dataset, index) => {
                    const referredColumn = dataset.columns.find((c) => c.key === dataset.referred_column_key);
                    return (
                        <li key={dataset.id} className="rounded-xl hover:bg-secondary">
                            <div className="flex items-center justify-between px-2">
                                <div className="flex gap-6 py-5">
                                    <Avatar square initials={dataset.name[0]} className="bg-zinc-300 text-gray-700 size-12" />
                                    <div>
                                        <div className="flex items-center gap-2 text-base/6 font-semibold">
                                            <a
                                                className="hover:underline"
                                                href={`/gp/ai-report-helper/datasets/view/${dataset.id}`}
                                                onClick={(e) => { e.preventDefault(); navigate(`/gp/ai-report-helper/datasets/view/${dataset.id}`); }}
                                            >
                                                {dataset.name}
                                            </a>
                                            {referredColumn && <Badge color="violet">Referred: {referredColumn.label}</Badge>}
                                        </div>
                                        <div className="text-xs/6 text-zinc-600">
                                            {dataset.columns.length} columns · {dataset.row_count ?? 0} rows
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Dropdown>
                                        <DropdownButton plain aria-label="More options">
                                            <EllipsisVerticalIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor="bottom end">
                                            <DropdownItem onClick={() => navigate(`/gp/ai-report-helper/datasets/view/${dataset.id}`)}>
                                                View
                                            </DropdownItem>
                                            <DropdownItem onClick={() => navigate(`/gp/ai-report-helper/datasets/edit/${dataset.id}`)}>
                                                Edit
                                            </DropdownItem>
                                            <DropdownItem onClick={() => handleOpenDeleteDialog(dataset.id)}>
                                                Delete
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalResult={filtered.length}
                dataName='Data Sets'
            />
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
            </FiltersDialog>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Data Set</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this data set? All of its rows will be deleted too. This action
                    cannot be undone, and any report column currently filled from it will stop resolving.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DatasetsPage;
